import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Save, Loader2, Mail, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import WhatsAppTemplatesSection from '../components/WhatsAppTemplatesSection';
import { useAuth } from '../contexts/AuthContext';

const VARIABLE_HINTS = {
  course_enrolled: '{reader_name} - اسم القارئ، {course_title} - اسم المساق',
  book_completed: '{reader_name}، {book_title} - اسم الكتاب، {course_title}',
  course_completed: '{reader_name}، {course_title}',
  reading_completed: '{reader_name}، {title} - عنوان القراءة',
  report_submitted: '{reader_name}، {classification} - التصنيف، {title} - العنوان، {pages_read} - عدد الصفحات',
};

const AdminTemplatesPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [edits, setEdits] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/admin/achievement-templates');
      setData(res.data);
      setEdits(JSON.parse(JSON.stringify(res.data.templates)));
    } catch { toast.error('فشل تحميل القوالب'); }
    finally { setLoading(false); }
  };

  const updateField = (et, field, value) => {
    setEdits(prev => ({ ...prev, [et]: { ...prev[et], [field]: value } }));
  };

  const saveTemplate = async (et) => {
    setSaving(et);
    try {
      await api.put(`/admin/achievement-templates/${et}`, edits[et]);
      toast.success('تم حفظ القالب بنجاح');
    } catch { toast.error('فشل حفظ القالب'); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-4xl mx-auto" data-testid="templates-page">
      <div>
        <h1 className="text-4xl font-bold text-accent">قوالب رسائل الإنجازات</h1>
        <p className="text-muted-foreground text-lg mt-1">تحرير الرسائل التي تصل القراء عند إنجازاتهم</p>
      </div>

      <WhatsAppTemplatesSection isMaster={user?.admin_role === 'مطلق'} />

      <div className="border-t pt-2">
        <p className="text-xs text-muted-foreground">قوالب الإنجازات الأخرى:</p>
      </div>

      {data.event_types.map(et => {
        const tpl = edits[et] || {};
        const label = data.labels[et];
        return (
          <Card key={et} className="p-6 space-y-4" data-testid={`template-${et}`}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-accent flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" /> {label}
              </h3>
              <button
                onClick={() => updateField(et, 'is_active', !tpl.is_active)}
                className="flex items-center gap-1.5 text-sm"
                data-testid={`toggle-${et}`}
              >
                {tpl.is_active ? (
                  <><ToggleRight className="w-6 h-6 text-green-500" /> <span className="text-green-600 font-medium">مفعّل</span></>
                ) : (
                  <><ToggleLeft className="w-6 h-6 text-gray-400" /> <span className="text-gray-500">معطّل</span></>
                )}
              </button>
            </div>

            <div>
              <label className="text-sm font-medium text-accent block mb-1">عنوان الرسالة</label>
              <input
                type="text"
                value={tpl.title || ''}
                onChange={e => updateField(et, 'title', e.target.value)}
                className="w-full px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary"
                data-testid={`title-${et}`}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-accent block mb-1">نص الرسالة</label>
              <textarea
                value={tpl.message || ''}
                onChange={e => updateField(et, 'message', e.target.value)}
                rows={8}
                className="w-full px-4 py-3 border rounded-xl text-sm leading-relaxed focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
                dir="rtl"
                data-testid={`message-${et}`}
              />
              <p className="text-xs text-muted-foreground mt-1">المتغيرات المتاحة: {VARIABLE_HINTS[et]}</p>
            </div>

            <Button onClick={() => saveTemplate(et)} disabled={saving === et} className="bg-primary hover:bg-primary/90" data-testid={`save-${et}`}>
              {saving === et ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Save className="w-4 h-4 ml-1" />}
              حفظ القالب
            </Button>
          </Card>
        );
      })}
    </div>
  );
};

export default AdminTemplatesPage;
